<template>
	<form ref="formRef" :action="action" :method="action && method" novalidate class="tw" @submit="handleSubmit">
		<slot></slot>
		<div
			class="empty:hidden"
			:class="{ 'm-auto w-full max-w-xs': centeredSubmitButtonSection, 'mt-14': !noSubmitButtonGap }"
		>
			<ValidationText
				v-if="isErrorVisible"
				class="mb-2"
				:class="{ 'text-center': centeredSubmitButtonSection }"
				:error-message="defaultErrorMessage"
				no-indent
			/>
			<slot name="submitButton" />
		</div>
	</form>
</template>

<script setup lang="ts">
import { computed, ref, provide, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import ValidationText from "./ValidationText.vue";

const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		action?: string;
		errorMessage?: string;
		method?: "post" | "get" | "dialog";
		showError?: boolean;
		centeredSubmitButtonSection?: boolean;
		noSubmitButtonGap?: boolean;
	}>(),
	{
		method: "post",
		showError: undefined,
	}
);

const emit = defineEmits<{
	(e: "submit", event: Event): void;
}>();

const formRef = ref<HTMLFormElement | null>(null);
const numberOfSubmits = ref(0);
const formValidationResetControl = ref(0);
const isFormValid = ref(true);

const isErrorVisible = computed(() =>
	props.showError !== undefined ? props.showError : numberOfSubmits.value > 0 && !isFormValid.value
);

const defaultErrorMessage = computed(() => {
	const translated = t("shared.form.validationError");
	return props.errorMessage || translated;
});

const updateFormValidity = () => {
	// E-Mail muss über sein Pattern geprüft werden und nicht über typeMismatch, was bei checkValidity() auch stattfindet
	let hasError = false;
	if (formRef.value?.elements) {
		for (const el of formRef.value.elements) {
			const element = el as HTMLFormElement;
			if (element.type === "email" && element.pattern) {
				const hasEmailError = element.validity.patternMismatch || element.validity.valueMissing;
				if (hasEmailError) hasError = true;
			} else {
				const isValid = element.checkValidity();
				if (!isValid) hasError = true;
			}
		}
	}
	return (isFormValid.value = !hasError);
};

onMounted(updateFormValidity);

const handleSubmit = (event: Event) => {
	numberOfSubmits.value += 1;
	if (updateFormValidity()) {
		if (!props.action) {
			event.preventDefault();
		}
		emit("submit", event);
	} else {
		focusFirstInvalidInput();
		event.preventDefault();
	}
};

function focusFirstInvalidInput() {
	if (formRef.value?.elements) {
		const invalidElement = Array.from(formRef.value.elements).find(
			(el) => !(el as HTMLFormElement).checkValidity()
		) as HTMLFormElement;
		invalidElement?.focus();
	}
}

provide("numberOfSubmits", numberOfSubmits);
provide("formValidationResetControl", formValidationResetControl);
provide("hasFormWrapper", true);
provide("updateFormValidity", updateFormValidity);
provide("formRef", formRef);

const resetFormValidation = () => {
	formValidationResetControl.value++;
	numberOfSubmits.value = 0;
};

defineExpose({
	resetFormValidation,
	formValidationResetControl,
	formRef,
	updateFormValidity,
	numberOfSubmits,
});
</script>
